// src/components/league/LeagueOverview.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Trophy, Award, ChevronLeft, ChevronRight, TrendingUp, Package, Swords } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PrizeDistribution } from './prize';
import TinkermanLeagueList from '../awards/TinkermanLeagueList';
import { useLeagueData, useManagerContext } from '../../context/DataContext';
import LeagueConstellation from './LeagueConstellation';

const LeagueOverview = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [heatmapPeriod, setHeatmapPeriod] = useState('all'); // 'all', 'early', 'middle', 'late'
  const [heatmapHighlight, setHeatmapHighlight] = useState(null);
  const [perfectWorld, setPerfectWorld] = useState(false);
  // Rivalry tab state — tracks which opponent has been selected for head-to-head comparison
  const [rivalryRival, setRivalryRival] = useState(null);
  const [hoveredMomentum, setHoveredMomentum] = useState(null); // { gw, cumDiff, idx, total }
  const navigate = useNavigate();
  const location = useLocation();
  const scrollRef = useRef(null);
  const pageRef = useRef(null);
  const heatmapRef = useRef(null);
  
  const { data } = useLeagueData();
  const { getManagers, selectedManager } = useManagerContext();
  
  // Helper function to update tab and keep location state in sync with menu
  const updateTab = (tab) => {
    setActiveTab(tab);
    // Update URL state so the navigation menu shows the correct item as active
    window.history.replaceState({ activeTab: tab }, "", location.pathname);
    
    // Dispatch a custom event for the menu to detect the tab change
    const tabChangeEvent = new CustomEvent('fpl-tab-changed', {
      detail: { tab, path: location.pathname }
    });
    window.dispatchEvent(tabChangeEvent);
  };
  
  // Effect to handle activeTab from navigation
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);
  
  // When tab changes, scroll to top of the page
  useEffect(() => {
    // Reset main page scroll
    if (pageRef.current) {
      pageRef.current.scrollTop = 0;
    }
    
    // Reset journey container scroll
    if (activeTab === 'ranking' && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [activeTab]);
  
  
  // Process the league data for easier access
  // If real data is not available, use sensible defaults
  const processLeagueData = () => {
    // Get all weekly points data for total points
    const allManagers = data?.weeklyPoints || [];
    
    // Sort managers by total points
    const sortedByTotalPoints = [...allManagers].sort((a, b) => {
      return (b.Total || 0) - (a.Total || 0);
    });
    
    // Extract captain points data
    const captainPointsData = data?.captainPoints || [];
    const sortedCaptainPoints = [...captainPointsData].sort((a, b) => {
      return (b.Total_Captain_Points || 0) - (a.Total_Captain_Points || 0);
    });
    
    // Extract loyalty issues data
    const loyaltyIssuesData = data?.loyaltyIssues?.summary || [];
    const sortedLoyaltyIssues = [...loyaltyIssuesData].sort((a, b) => {
      return (b.Total_Points_Missed || 0) - (a.Total_Points_Missed || 0);
    });
    
    // Extract differential players data
    const differentialPlayersData = data?.differentialPlayers?.summary || [];
    const sortedDifferentialPlayers = [...differentialPlayersData].sort((a, b) => {
      return (b.Differential_Points || 0) - (a.Differential_Points || 0);
    });
    
    // Extract most improved data from half season comparison
    const halfSeasonComparisonData = data?.halfSeasonComparison || [];
    const sortedImprovement = [...halfSeasonComparisonData].sort((a, b) => {
      return Number(b.Points_Difference) - Number(a.Points_Difference);
    });

    // Extract bench points data
    const benchPointsData = data?.benchPoints || [];
    const sortedBenchPoints = [...benchPointsData].sort((a, b) => {
      return (b.Points_on_Bench || 0) - (a.Points_on_Bench || 0);
    });
    
    return {
      totalPoints: sortedByTotalPoints,
      captainPoints: sortedCaptainPoints,
      loyaltyIssues: sortedLoyaltyIssues,
      differentialPoints: sortedDifferentialPlayers,
      mostImproved: sortedImprovement,
      benchPoints: sortedBenchPoints
    };
  };
  
  const performanceData = processLeagueData();

  // Helper function to get gameweek data for all managers for the heatmap
  const getGameweekData = () => {
    if (!data?.weeklyPoints || data.weeklyPoints.length === 0) {
      return { managers: [], gameweeks: [], relativeScores: {}, averages: {} };
    }
    
    // Get all managers
    const managers = data.weeklyPoints.map(row => row.Manager);
    
    // Get all gameweek numbers (GW1, GW2, etc.)
    const gameweekKeys = Object.keys(data.weeklyPoints[0] || {})
      .filter(key => !isNaN(parseInt(key)))
      .map(key => parseInt(key))
      .sort((a, b) => a - b);
    
    // Calculate average scores for each gameweek
    const averages = {};
    gameweekKeys.forEach(gw => {
      let totalPoints = 0;
      let managerCount = 0;
      
      // Sum up points for this gameweek
      data.weeklyPoints.forEach(manager => {
        const gwScore = parseInt(manager[gw]);
        if (!isNaN(gwScore)) {
          totalPoints += gwScore;
          managerCount++;
        }
      });
      
      // Calculate average if we have data
      if (managerCount > 0) {
        averages[gw] = Math.round(totalPoints / managerCount);
      }
    });
    
    // Calculate relative scores (compared to average)
    const relativeScores = {};
    
    managers.forEach(manager => {
      relativeScores[manager] = {};
      
      gameweekKeys.forEach(gw => {
        const managerData = data.weeklyPoints.find(row => row.Manager === manager);
        if (managerData) {
          const score = parseInt(managerData[gw]);
          const avg = averages[gw] || 0;
          
          if (!isNaN(score)) {
            // Calculate difference from average
            relativeScores[manager][gw] = score - avg;
          }
        }
      });
    });
    
    return { 
      managers, 
      gameweeks: gameweekKeys,
      relativeScores,
      averages
    };
  };
  
  // Helper function to get background colour based on performance (returns inline style object)
  const getPerformanceColor = (diff) => {
    if (diff === undefined || diff === null) return { backgroundColor: 'rgba(26,26,26,0.4)' };

    // Define colour scale based on points difference
    if (diff > 20) return { backgroundColor: 'rgba(33,150,83,0.9)' };
    if (diff > 10) return { backgroundColor: 'rgba(33,150,83,0.7)' };
    if (diff > 5)  return { backgroundColor: 'rgba(33,150,83,0.5)' };
    if (diff > 0)  return { backgroundColor: 'rgba(68,213,163,0.35)' };
    if (diff === 0) return { backgroundColor: 'rgba(74,74,74,0.5)' };
    if (diff > -5)  return { backgroundColor: 'rgba(235,87,87,0.35)' };
    if (diff > -10) return { backgroundColor: 'rgba(235,87,87,0.5)' };
    if (diff > -20) return { backgroundColor: 'rgba(235,87,87,0.7)' };
    return { backgroundColor: 'rgba(235,87,87,0.9)' };
  };

  // Helper function to get text colour based on performance value
  const getTextColor = (diff) => {
    if (diff === undefined || diff === null) return { color: 'var(--c-grey)' };

    // For darker backgrounds use white text
    if (diff > 20 || diff < -10) return { color: 'white' };

    // For lighter backgrounds use muted text
    return { color: 'white' };
  };
  
  const goToMyJourney = () => {
    // Navigate to the My Profile tab in My Journey instead of transfer-regrets
    navigate('/my-journey', { state: { activeTab: 'profile' } });
  };
  
  // Helper function for ordinal suffixes
  const getOrdinalSuffix = (num) => {
    if (!num) return "";
    
    const j = num % 10;
    const k = num % 100;
    
    if (j === 1 && k !== 11) return "st";
    if (j === 2 && k !== 12) return "nd";
    if (j === 3 && k !== 13) return "rd";
    return "th";
  };
  
  const renderOverviewTab = () => {
    const champion    = performanceData.totalPoints[0];
    const runnerUp    = performanceData.totalPoints[1];
    const third       = performanceData.totalPoints[2];
    const captainKing = performanceData.captainPoints[0];
    const benchKing   = performanceData.benchPoints[0];
    const regretKing  = performanceData.loyaltyIssues[0];
    const diffKing    = performanceData.differentialPoints[0];
    const surgeKing   = performanceData.mostImproved.find(m => Number(m.Points_Difference) > 0);

    // League-wide totals from weeklyPoints
    const allGWData   = data?.weeklyPoints || [];
    const totalHits   = allGWData.reduce((sum, m) => sum + Math.round((m.Total_Transfer_Cost || 0) / 4), 0);

    const gwsPlayed = allGWData.length > 0
      ? Object.keys(allGWData[0]).filter(k => !isNaN(parseInt(k)) && parseInt(allGWData[0][k]) > 0).length
      : 0;

    // Average points per manager per GW — a league-level quality metric
    const leagueTotalPts = allGWData.reduce((sum, m) => sum + (m.Total || 0), 0);
    const avgPtsPerGW = allGWData.length > 0 && gwsPlayed > 0
      ? Math.round(leagueTotalPts / (allGWData.length * gwsPlayed))
      : null;

    // Highest single-GW score across the league
    let highestGW = 0, highestGWManager = '', highestGWNum = 0;
    allGWData.forEach(m => {
      Object.keys(m).forEach(k => {
        const gw = parseInt(k);
        if (!isNaN(gw)) {
          const pts = parseInt(m[k]);
          if (!isNaN(pts) && pts > highestGW) {
            highestGW = pts; highestGWManager = m.Manager; highestGWNum = gw;
          }
        }
      });
    });

    const hitMerchant = allGWData.slice().sort((a, b) => (b.Total_Transfer_Cost || 0) - (a.Total_Transfer_Cost || 0))[0];
    const hitCount    = Math.round((hitMerchant?.Total_Transfer_Cost || 0) / 4);

    const superlatives = [
      { colour: '#f2c94c',          label: 'Armband King',      name: captainKing?.Manager, stat: captainKing?.Total_Captain_Points, unit: 'captain pts' },
      { colour: 'var(--c-bad)',     label: 'Bench Disaster',    name: benchKing?.Manager,   stat: benchKing?.Points_on_Bench,       unit: 'pts on bench' },
      { colour: '#eb8c57',          label: 'Transfer Regrets',  name: regretKing?.Manager,  stat: regretKing?.Total_Points_Missed,  unit: 'pts missed' },
      { colour: 'var(--c-turquoise)', label: 'Differential King', name: diffKing?.Manager,  stat: diffKing?.Differential_Points,    unit: 'diff pts' },
      { colour: 'var(--c-blue)',    label: 'Second-Half Surge', name: surgeKing?.Manager,   stat: surgeKing?.Points_Difference > 0 ? `+${surgeKing.Points_Difference}` : surgeKing?.Points_Difference, unit: 'pts gained H2' },
      { colour: 'var(--c-cyan)',    label: 'Hit Merchant',      name: hitMerchant?.Manager, stat: hitCount,                         unit: 'hits taken' },
    ].filter(s => s.name);

    const podiumRankMeta = [
      { rank: '2', colour: '#c0c0c0', label: '2nd', m: runnerUp },
      { rank: '3', colour: '#cd7f32', label: '3rd', m: third },
    ].filter(x => x.m);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* ── Champion spotlight ──────────────────────────────────────────────── */}
        {champion && (
          <div style={{
            position: 'relative', overflow: 'hidden',
            borderRadius: '16px', padding: '24px',
            background: 'linear-gradient(135deg, rgba(242,201,76,0.07) 0%, rgba(255,255,255,0.03) 60%)',
            border: '1px solid rgba(242,201,76,0.18)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
          }}>
            {/* Subtle gradient sweep — replaces thick top border */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, #f2c94c44, #f2c94c22, transparent)', pointerEvents: 'none' }} />

            <p style={{ fontSize: '10px', fontWeight: 700, color: '#f2c94c', textTransform: 'uppercase', letterSpacing: '0.14em', margin: '0 0 10px' }}>
              League Champion {new Date().getFullYear()}
            </p>
            <p style={{ fontSize: '34px', fontWeight: 900, color: 'white', margin: '0 0 2px', lineHeight: 1.05, letterSpacing: '-0.5px' }}>
              {champion.Manager}
            </p>
            <p style={{ fontSize: '26px', fontWeight: 900, color: '#f2c94c', margin: '0 0 18px', lineHeight: 1 }}>
              {(champion.Total || 0).toLocaleString()} <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--c-grey)' }}>pts</span>
            </p>

            {/* Runner-up + 3rd */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              {podiumRankMeta.map(({ rank, colour, label, m }) => (
                <div key={m.Manager} style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '5px 12px 5px 8px', borderRadius: '8px',
                  backgroundColor: 'var(--c-surface-3)', border: '1px solid var(--c-border)',
                }}>
                  <div style={{
                    width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                    backgroundColor: colour + '22', border: `1px solid ${colour}66`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <span style={{ fontSize: '11px', fontWeight: 800, color: colour, lineHeight: 1 }}>{rank}</span>
                  </div>
                  <div>
                    <p style={{ fontSize: '10px', color: 'var(--c-grey)', margin: 0, lineHeight: 1 }}>{label}</p>
                    <p style={{ fontSize: '13px', fontWeight: 700, color: 'white', margin: '1px 0 0' }}>{m.Manager}</p>
                  </div>
                  <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--c-grey)', margin: '0 0 0 4px' }}>{(m.Total || 0).toLocaleString()}</p>
                </div>
              ))}
              <button
                onClick={() => updateTab('leaderboard')}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  padding: '5px 12px', borderRadius: '8px', cursor: 'pointer',
                  backgroundColor: 'transparent', border: '1px solid var(--c-border)',
                  fontSize: '12px', color: 'var(--c-cyan)', fontWeight: 600,
                }}
              >
                Full table <ChevronRight size={13} />
              </button>
            </div>
          </div>
        )}

        {/* ── Season in numbers ───────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
          {[
            { label: 'Avg pts / GW',     value: avgPtsPerGW ?? '—',                                                sub: 'per manager',     accent: 'var(--c-turquoise)' },
            { label: 'Title margin',      value: champion && runnerUp ? `${(champion.Total || 0) - (runnerUp.Total || 0)} pts` : '—', sub: `${champion?.Manager ?? ''} over ${runnerUp?.Manager ?? ''}`, accent: '#f2c94c' },
            { label: 'Highest GW score', value: `${highestGW}`,                                                   sub: `${highestGWManager} · GW${highestGWNum}`, accent: 'var(--c-blue)' },
            { label: 'Transfer hits',    value: totalHits,                                                         sub: 'across the league', accent: 'var(--c-bad)' },
          ].map(({ label, value, accent, sub }) => (
            <div key={label} style={{
              position: 'relative', overflow: 'hidden',
              padding: '14px 16px', borderRadius: '10px',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
            }}>
              {/* Colour-coded accent sweep at top — replaces thick border */}
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: `linear-gradient(90deg, ${accent}55, ${accent}22, transparent)`, pointerEvents: 'none' }} />
              <p style={{ fontSize: '10px', fontWeight: 700, color: 'var(--c-grey)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>{label}</p>
              <p style={{ fontSize: '24px', fontWeight: 900, color: accent, margin: 0, lineHeight: 1 }}>{value}</p>
              {sub && <p style={{ fontSize: '10px', color: 'var(--c-grey)', margin: '5px 0 0', lineHeight: 1.3 }}>{sub}</p>}
            </div>
          ))}
        </div>

        {/* ── Superlatives ────────────────────────────────────────────────────── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <p style={{ fontSize: '10px', fontWeight: 700, color: 'var(--c-grey)', textTransform: 'uppercase', letterSpacing: '0.12em', margin: 0 }}>Season awards</p>
            <button
              onClick={() => updateTab('specialStats')}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '4px 10px', borderRadius: '6px', cursor: 'pointer',
                backgroundColor: 'transparent', border: '1px solid var(--c-border)',
                fontSize: '11px', color: 'var(--c-grey)', fontWeight: 600,
              }}
            >
              Full stats <ChevronRight size={11} />
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))', gap: '8px' }}>
            {superlatives.map(({ colour, label, name, stat, unit }) => (
              <div key={label} style={{
                padding: '14px', borderRadius: '10px',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.07)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
                display: 'flex', flexDirection: 'column', gap: '5px',
                position: 'relative', overflow: 'hidden',
              }}>
                {/* 1px gradient line at top — subtle colour coding without thick borders */}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: `linear-gradient(90deg, ${colour}66, ${colour}22, transparent)`, pointerEvents: 'none' }} />
                <p style={{ fontSize: '10px', fontWeight: 700, color: colour, textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0, opacity: 0.85 }}>{label}</p>
                <p style={{ fontSize: '14px', fontWeight: 800, color: 'white', margin: 0, lineHeight: 1.2 }}>{name}</p>
                <p style={{ fontSize: '22px', fontWeight: 900, color: colour, margin: 0, lineHeight: 1 }}>
                  {typeof stat === 'number' ? stat.toLocaleString() : stat}
                  <span style={{ fontSize: '10px', fontWeight: 500, color: 'var(--c-grey)', marginLeft: '4px' }}>{unit}</span>
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Divider ─────────────────────────────────────────────────────────── */}
        <div style={{ height: '1px', backgroundColor: 'var(--c-border)', margin: '4px 0' }} />

        {/* ── Prize distribution ──────────────────────────────────────────────── */}
        <PrizeDistribution />

        {/* ── Nav ────────────────────────────────────────────────────────────── */}
        <div className="flex justify-between mt-2">
          <button
            onClick={goToMyJourney}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all"
            style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid var(--c-border)', color: 'var(--c-grey)' }}
          >
            <ChevronLeft size={16} /> My Journey
          </button>
          <button
            onClick={() => updateTab('leaderboard')}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white transition-all"
            style={{ backgroundColor: 'var(--c-blue)' }}
          >
            Leaderboard <ArrowRight size={14} />
          </button>
        </div>
      </div>
    );
  };
  
  const renderLeaderboardTab = () => {
    // Build perfect-world standings: actual points + transfer missed + captain missed
    const perfectStandings = (() => {
      const loyaltyMap = {};
      (data?.loyaltyIssues?.summary || []).forEach((r) => {
        loyaltyMap[r.Manager] = r.Total_Points_Missed || 0;
      });
      const captainMap = {};
      (data?.captainHindsight?.summary || []).forEach((r) => {
        captainMap[r.Manager] = r.Points_Missed || 0;
      });
      return [...performanceData.totalPoints]
        .map((m) => {
          const transfersMissed = loyaltyMap[m.Manager] || 0;
          const captainMissed   = captainMap[m.Manager]  || 0;
          return {
            ...m,
            perfectTotal: (m.Total || 0) + transfersMissed + captainMissed,
            pointsMissed: transfersMissed + captainMissed,
            transfersMissed,
            captainMissed,
          };
        })
        .sort((a, b) => b.perfectTotal - a.perfectTotal);
    })();

    // Real standings indexed by manager for rank-delta computation
    const realRankMap = {};
    performanceData.totalPoints.forEach((m, i) => { realRankMap[m.Manager] = i + 1; });

    const displayList = perfectWorld ? perfectStandings : performanceData.totalPoints;
    const topScore = perfectWorld
      ? (displayList[0]?.perfectTotal || 1)
      : (displayList[0]?.Total || 1);

    const getScore  = (m) => perfectWorld ? m.perfectTotal : (m.Total || 0);
    const MEDAL     = ['#f2c94c', '#c0c0c0', '#cd7f32'];
    const MEDAL_BG  = ['rgba(242,201,76,0.12)', 'rgba(192,192,192,0.10)', 'rgba(205,127,50,0.10)'];

    // ── Podium (top 3) ──────────────────────────────────────────────────────────
    const podiumOrder = displayList.length >= 3
      ? [displayList[1], displayList[0], displayList[2]]  // 2nd | 1st | 3rd
      : displayList.slice(0, 3);
    const podiumHeights = [80, 110, 60]; // 2nd | 1st | 3rd
    const podiumRanks   = [2, 1, 3];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h3 style={{ fontSize: '20px', fontWeight: 800, color: 'white', margin: 0 }}>
              {perfectWorld ? 'In Another World…' : 'Full League Table'}
            </h3>
            {perfectWorld && (
              <p style={{ fontSize: '12px', color: 'var(--c-grey)', margin: '4px 0 0', whiteSpace: 'nowrap' }}>
                If every transfer and captain pick had been optimal — actual points plus decisions left on the table.
              </p>
            )}
          </div>
          <button
            onClick={() => setPerfectWorld((v) => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', borderRadius: '20px',
              fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
              ...(perfectWorld
                ? { backgroundColor: 'rgba(68,213,163,0.15)', color: 'var(--c-turquoise)', border: '1px solid rgba(68,213,163,0.4)' }
                : { backgroundColor: 'var(--c-surface-3)', color: 'var(--c-grey)', border: '1px solid var(--c-border)' }),
            }}
          >
            <span style={{ fontSize: '16px' }}>{perfectWorld ? '✦' : '✧'}</span>
            {perfectWorld ? 'Back to reality' : 'In Another World…'}
          </button>
        </div>

        {/* ── Podium (top 3) ─────────────────────────────────────────────────── */}
        {displayList.length >= 3 && (
          <div style={{
            backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)',
            borderRadius: '16px', padding: '24px 16px 0',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '8px', marginBottom: 0 }}>
              {podiumOrder.map((manager, i) => {
                const rank = podiumRanks[i];
                const score = getScore(manager);
                const isSelf = manager.Manager === selectedManager;
                return (
                  <div key={manager.Manager} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, maxWidth: '160px' }}>
                    {/* Name + score above the platform */}
                    <div style={{ textAlign: 'center', marginBottom: '8px', padding: '0 4px' }}>
                      <p style={{
                        fontSize: '13px', fontWeight: 700,
                        color: isSelf ? 'var(--c-turquoise)' : 'white',
                        margin: '0 0 2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px',
                      }}>{manager.Manager}</p>
                      <p style={{ fontSize: '18px', fontWeight: 900, color: MEDAL[rank - 1], margin: 0, lineHeight: 1 }}>
                        {score.toLocaleString()}
                      </p>
                      <p style={{ fontSize: '10px', color: 'var(--c-grey)', margin: '2px 0 0' }}>pts</p>
                      {perfectWorld && manager.pointsMissed > 0 && (
                        <p style={{ fontSize: '10px', color: 'var(--c-turquoise)', margin: '3px 0 0', fontWeight: 600 }}>
                          +{manager.pointsMissed} recovered
                        </p>
                      )}
                    </div>
                    {/* Platform */}
                    <div style={{
                      width: '100%', height: `${podiumHeights[i]}px`,
                      backgroundColor: MEDAL_BG[rank - 1],
                      border: `1px solid ${MEDAL[rank - 1]}44`,
                      borderBottom: 'none',
                      borderRadius: '8px 8px 0 0',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span style={{ fontSize: rank === 1 ? '32px' : '24px' }}>
                        {rank === 1 ? '🥇' : rank === 2 ? '🥈' : '🥉'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Ranked rows (4th onwards) ───────────────────────────────────────── */}
        <div style={{
          backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)',
          borderRadius: '16px', overflow: 'hidden',
        }}>
          {displayList.slice(3).map((manager, i) => {
            const rank       = i + 4;
            const score      = getScore(manager);
            const barWidth   = Math.round((score / topScore) * 100);
            const isSelf     = manager.Manager === selectedManager;
            const realRank   = realRankMap[manager.Manager] || rank;
            const rankDelta  = perfectWorld ? realRank - rank : 0; // positive = moved up

            return (
              <div
                key={manager.Manager}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '12px 16px',
                  background: isSelf ? 'rgba(63,105,174,0.10)' : 'transparent',
                  borderRadius: isSelf ? '8px' : '0',
                  borderBottom: i < displayList.length - 4 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                  transition: 'background 0.2s',
                }}
              >
                {/* Rank number */}
                <span style={{ width: '24px', fontSize: '13px', fontWeight: 700, color: 'var(--c-grey)', flexShrink: 0, textAlign: 'right' }}>
                  {rank}
                </span>

                {/* Name + bar */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', fontWeight: isSelf ? 700 : 500, color: isSelf ? 'white' : 'var(--c-grey)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {manager.Manager}
                    </span>
                    {/* Rank delta badge in Another World mode */}
                    {perfectWorld && rankDelta !== 0 && (
                      <span style={{
                        fontSize: '10px', fontWeight: 700, flexShrink: 0,
                        color: rankDelta > 0 ? 'var(--c-turquoise)' : 'var(--c-bad)',
                        backgroundColor: rankDelta > 0 ? 'rgba(68,213,163,0.12)' : 'rgba(235,87,87,0.12)',
                        border: `1px solid ${rankDelta > 0 ? 'rgba(68,213,163,0.3)' : 'rgba(235,87,87,0.3)'}`,
                        borderRadius: '4px', padding: '1px 5px',
                      }}>
                        {rankDelta > 0 ? `↑${rankDelta}` : `↓${Math.abs(rankDelta)}`}
                      </span>
                    )}
                  </div>
                  {/* Points bar */}
                  <div style={{ height: '4px', backgroundColor: 'var(--c-surface-3)', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width: `${barWidth}%`, borderRadius: '2px',
                      backgroundColor: isSelf ? 'var(--c-blue)' : 'var(--c-border)',
                      transition: 'width 0.6s ease',
                    }} />
                  </div>
                </div>

                {/* Score */}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <span style={{ fontSize: '14px', fontWeight: 800, color: isSelf ? 'white' : 'var(--c-grey)' }}>
                    {score.toLocaleString()}
                  </span>
                  {perfectWorld && manager.pointsMissed > 0 && (
                    <p style={{ fontSize: '10px', color: 'var(--c-turquoise)', margin: '1px 0 0', fontWeight: 600 }}>
                      +{manager.pointsMissed}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Nav buttons ────────────────────────────────────────────────────── */}
        <div className="flex justify-between mt-4">
          <button
            onClick={() => updateTab('overview')}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all"
            style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid var(--c-border)', color: 'var(--c-grey)' }}
          >
            <ChevronLeft size={16} /> Overview
          </button>
          <button
            onClick={() => updateTab('heatmap')}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white transition-all"
            style={{ backgroundColor: 'var(--c-blue)' }}
          >
            Season Heatmap <ArrowRight size={14} />
          </button>
        </div>
      </div>
    );
  };

  // Render the Heatmap tab
  const renderHeatmapTab = () => {
    const { managers, gameweeks, relativeScores } = getGameweekData();
    
    // Filter gameweeks based on selected period
    let filteredGameweeks = [...gameweeks];
    
    if (heatmapPeriod === 'early') {
      // First third of the season
      const cutoff = Math.ceil(gameweeks.length / 3);
      filteredGameweeks = gameweeks.slice(0, cutoff);
    } else if (heatmapPeriod === 'middle') {
      // Middle third of the season
      const oneThird = Math.ceil(gameweeks.length / 3);
      const twoThirds = Math.ceil(2 * gameweeks.length / 3);
      filteredGameweeks = gameweeks.slice(oneThird, twoThirds);
    } else if (heatmapPeriod === 'late') {
      // Final third of the season
      const twoThirds = Math.ceil(2 * gameweeks.length / 3);
      filteredGameweeks = gameweeks.slice(twoThirds);
    }
    
    // Calculate average performance across all weeks for each manager
    const managerAverages = {};
    managers.forEach(manager => {
      let totalDiff = 0;
      let weekCount = 0;
      
      filteredGameweeks.forEach(gw => {
        if (relativeScores[manager] && relativeScores[manager][gw] !== undefined) {
          totalDiff += relativeScores[manager][gw];
          weekCount++;
        }
      });
      
      managerAverages[manager] = weekCount > 0 ? Math.round(totalDiff / weekCount) : 0;
    });
    
    // Sort managers by average performance
    const sortedManagers = [...managers].sort((a, b) => {
      return managerAverages[b] - managerAverages[a];
    });
    
    return (
      <div className="space-y-6">
        <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}>
          <h3 className="text-xl font-bold mb-4 flex items-center">
            <div className="w-6 h-6 rounded-md mr-2" style={{ background: 'linear-gradient(to right, var(--c-bad), var(--c-good))' }}></div>
            Season Performance Heatmap
          </h3>

          <p className="mb-3" style={{ color: 'var(--c-grey)' }}>
            See how each manager performed compared to the league average each gameweek.
            Green cells indicate above average performances, red cells indicate below average.
          </p>

          {/* Click tip — prominent, before the filters */}
          <p className="mb-5 text-xs font-medium" style={{ color: 'var(--c-cyan)' }}>
            👆 Click any manager name to highlight their row across the season.
          </p>

          {/* Quick Insights — stat chips above the heatmap */}
          {sortedManagers.length > 0 && (
            <div className="flex flex-wrap gap-3 mb-6">
              {/* Best performer */}
              <div className="flex-1 min-w-[160px] rounded-xl px-4 py-3" style={{ backgroundColor: 'var(--c-surface-1)', border: '1px solid var(--c-border)' }}>
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--c-grey)' }}>Top performer</p>
                <p className="font-bold text-white text-sm truncate">{sortedManagers[0]}</p>
                <p className="text-xl font-black mt-0.5" style={{ color: 'var(--c-turquoise)' }}>
                  +{managerAverages[sortedManagers[0]]} <span className="text-xs font-medium" style={{ color: 'var(--c-grey)' }}>pts / GW vs avg</span>
                </p>
              </div>

              {/* Worst performer */}
              <div className="flex-1 min-w-[160px] rounded-xl px-4 py-3" style={{ backgroundColor: 'var(--c-surface-1)', border: '1px solid var(--c-border)' }}>
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--c-grey)' }}>Struggled most</p>
                <p className="font-bold text-white text-sm truncate">{sortedManagers[sortedManagers.length - 1]}</p>
                <p className="text-xl font-black mt-0.5" style={{ color: 'var(--c-bad)' }}>
                  {managerAverages[sortedManagers[sortedManagers.length - 1]] > 0 ? '+' : ''}{managerAverages[sortedManagers[sortedManagers.length - 1]]} <span className="text-xs font-medium" style={{ color: 'var(--c-grey)' }}>pts / GW vs avg</span>
                </p>
              </div>

              {/* Your performance */}
              {selectedManager && (
                <div className="flex-1 min-w-[160px] rounded-xl px-4 py-3" style={{ backgroundColor: 'var(--c-surface-1)', border: `1px solid ${managerAverages[selectedManager] >= 0 ? 'rgba(68,213,163,0.35)' : 'rgba(235,87,87,0.35)'}` }}>
                  <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--c-grey)' }}>Your average</p>
                  <p className="font-bold text-white text-sm truncate">{selectedManager}</p>
                  <p className="text-xl font-black mt-0.5" style={{ color: managerAverages[selectedManager] >= 0 ? 'var(--c-turquoise)' : 'var(--c-bad)' }}>
                    {managerAverages[selectedManager] > 0 ? '+' : ''}{managerAverages[selectedManager]} <span className="text-xs font-medium" style={{ color: 'var(--c-grey)' }}>pts / GW vs avg</span>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Period filter buttons */}
          <div className="flex flex-wrap justify-center mb-6 gap-2">
            {(['all', 'early', 'middle', 'late']).map((period) => {
              const labels = { all: 'Full Season', early: 'Early Season', middle: 'Mid Season', late: 'Late Season' };
              const isActive = heatmapPeriod === period;
              return (
                <button
                  key={period}
                  onClick={() => setHeatmapPeriod(period)}
                  className="px-3 py-1.5 rounded-full text-sm"
                  style={isActive
                    ? { backgroundColor: 'var(--c-blue)', color: 'white' }
                    : { backgroundColor: 'var(--c-surface-3)', border: '1px solid var(--c-border)', color: 'var(--c-grey)' }
                  }
                >
                  {labels[period]}
                </button>
              );
            })}
          </div>

          {/* Heatmap colour legend */}
          <div className="rounded-lg p-4 mb-6" style={{ backgroundColor: 'var(--c-surface-1)', border: '1px solid var(--c-border)' }}>
            <h4 className="text-center text-sm mb-3 font-medium" style={{ color: 'var(--c-grey)' }}>Performance vs. League Average</h4>
            <div className="flex justify-center items-center">
              <div className="flex items-center">
                <div className="h-4 w-24 rounded-l-md" style={{ background: 'linear-gradient(to right, rgba(235,87,87,0.9), rgba(235,87,87,0.35), rgba(74,74,74,0.5))' }}></div>
                <div className="h-4 w-24 rounded-r-md" style={{ background: 'linear-gradient(to right, rgba(74,74,74,0.5), rgba(68,213,163,0.35), rgba(33,150,83,0.9))' }}></div>
              </div>
            </div>
            <div className="flex justify-between text-xs mt-1 px-1" style={{ color: 'var(--c-grey)' }}>
              <span>-20+ pts</span>
              <span>Below Avg</span>
              <span>Average</span>
              <span>Above Avg</span>
              <span>+20+ pts</span>
            </div>
          </div>

          {/* Scrollable heatmap container with animation */}
          <div
            ref={heatmapRef}
            className="overflow-x-auto max-w-full rounded-lg"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'var(--c-border) var(--c-surface-1)',
              animation: 'fadeIn 0.5s ease-out'
            }}
          >
            <div className="min-w-max">
              {/* Heatmap header */}
              <div className="grid grid-flow-col auto-cols-min">
                {/* Empty cell for manager names column */}
                <div
                  className="sticky left-0 z-10 p-2 w-[120px] text-center font-medium"
                  style={{ backgroundColor: 'var(--c-surface-1)', color: 'var(--c-grey)', borderBottom: '1px solid var(--c-border)', borderRight: '1px solid var(--c-border)' }}
                >
                  Manager
                </div>

                {/* Gameweek headers */}
                {filteredGameweeks.map(gw => (
                  <div
                    key={gw}
                    className="p-1.5 text-center text-xs font-medium w-6"
                    style={{ color: 'var(--c-grey)', borderBottom: '1px solid var(--c-border)', borderRight: '1px solid var(--c-border)' }}
                  >
                    {gw}
                  </div>
                ))}

                {/* Average column */}
                <div
                  className="p-1.5 text-center text-xs font-medium w-14"
                  style={{ color: 'var(--c-grey)', borderBottom: '1px solid var(--c-border)', borderRight: '1px solid var(--c-border)' }}
                >
                  Avg
                </div>
              </div>
              
              {/* Heatmap rows */}
              {sortedManagers.map((manager, rowIndex) => {
                const isHighlighted = heatmapHighlight === manager;
                const isSelected = manager === selectedManager;
                const rowBg = isHighlighted
                  ? 'rgba(63,105,174,0.25)'
                  : isSelected
                    ? 'rgba(63,105,174,0.15)'
                    : rowIndex % 2 === 0
                      ? 'rgba(29,29,29,0.6)'
                      : 'transparent';

                return (
                  <div
                    key={manager}
                    className="grid grid-flow-col auto-cols-min transition-all duration-200"
                    style={{
                      backgroundColor: rowBg,
                      outline: isHighlighted ? '1px solid var(--c-blue)' : 'none',
                      transform: isHighlighted ? 'scale(1.01)' : 'scale(1)',
                      zIndex: isHighlighted ? 10 : 'auto',
                      animation: `fadeIn 0.3s ease-out ${rowIndex * 0.03}s both`,
                    }}
                  >
                    {/* Manager name (sticky left column) */}
                    <div
                      className="sticky left-0 z-10 p-2 w-[120px] text-xs truncate cursor-pointer transition-colors"
                      style={{
                        backgroundColor: isHighlighted
                          ? 'rgba(63,105,174,0.5)'
                          : isSelected
                            ? 'rgba(63,105,174,0.35)'
                            : 'var(--c-surface-1)',
                        color: isHighlighted || isSelected ? 'white' : 'var(--c-grey)',
                        fontWeight: isHighlighted ? 700 : isSelected ? 500 : 400,
                        borderBottom: '1px solid var(--c-border)',
                        borderRight: '1px solid var(--c-border)',
                        borderLeft: isHighlighted ? '2px solid var(--c-blue)' : '2px solid transparent',
                      }}
                      onClick={() => setHeatmapHighlight(heatmapHighlight === manager ? null : manager)}
                    >
                      {manager}
                    </div>

                    {/* Gameweek cells */}
                    {filteredGameweeks.map(gw => {
                      const diff = relativeScores[manager]?.[gw];
                      return (
                        <div
                          key={`${manager}-${gw}`}
                          className="w-6 h-6"
                          title={`${manager} in GW${gw}: ${diff !== undefined ? (diff > 0 ? '+' : '') + diff + ' vs. avg' : 'No data'}`}
                          style={{
                            ...getPerformanceColor(diff),
                            borderBottom: '1px solid var(--c-border)',
                            borderRight: '1px solid var(--c-border)',
                            outline: isHighlighted ? '1px solid var(--c-blue)' : 'none',
                            transition: 'all 0.2s ease-in-out',
                            transform: (isHighlighted || isSelected) ? 'scale(1.1)' : 'scale(1)',
                          }}
                        />
                      );
                    })}

                    {/* Average performance column */}
                    <div
                      className="w-14 h-6 flex items-center justify-center text-xs font-medium"
                      title={`${manager}'s average: ${managerAverages[manager] > 0 ? '+' : ''}${managerAverages[manager]} vs. league avg`}
                      style={{
                        ...getPerformanceColor(managerAverages[manager]),
                        ...getTextColor(managerAverages[manager]),
                        fontWeight: isHighlighted ? 700 : undefined,
                        borderBottom: '1px solid var(--c-border)',
                        borderRight: '1px solid var(--c-border)',
                        outline: isHighlighted ? '1px solid var(--c-blue)' : 'none',
                        transition: 'all 0.2s ease-in-out',
                        transform: (isHighlighted || isSelected) ? 'scale(1.05)' : 'scale(1)',
                      }}
                    >
                      {managerAverages[manager] > 0 ? '+' : ''}{managerAverages[manager]}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
        </div>

        <div className="flex justify-between mt-4">
          <button
            onClick={() => updateTab('leaderboard')}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all"
            style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid var(--c-border)', color: 'var(--c-grey)' }}
          >
            <ChevronLeft size={16} /> Leaderboard
          </button>
          <button
            onClick={() => updateTab('specialStats')}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white transition-all"
            style={{ backgroundColor: 'var(--c-blue)' }}
          >
            Special Stats <ArrowRight size={14} />
          </button>
        </div>
      </div>
    );
  };
  
  // Special Stats tab
  const renderSpecialStatsTab = () => (
    <div className="space-y-6">
      <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}>
        <h3 className="text-xl font-bold mb-4 flex items-center">
          <Award className="mr-2" size={20} style={{ color: 'var(--c-yellow)' }} />
          Special Awards &amp; Statistics
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Captain Genius */}
          <div className="rounded-lg p-5" style={{ backgroundColor: 'var(--c-surface-1)', border: '1px solid var(--c-border)' }}>
            <h4 className="font-medium mb-3 flex items-center" style={{ color: 'var(--c-yellow)' }}>
              <Trophy className="mr-2" size={16} />
              Captain Genius
            </h4>

            <p className="text-xs mt-1 mb-3" style={{ color: 'var(--c-grey)' }}>Managers who earned the most points from their captain selections throughout the season.</p>

            {performanceData.captainPoints.length > 0 ? (
              <div className="space-y-3">
                {performanceData.captainPoints.slice(0, 3).map((manager, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg"
                    style={{
                      backgroundColor: index === 0 ? 'rgba(242,201,76,0.12)' : 'var(--c-surface-2)',
                      border: `1px solid ${index === 0 ? 'var(--c-yellow)' : 'var(--c-border)'}`,
                    }}
                  >
                    <div className="flex items-center">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center mr-2 text-xs font-bold"
                        style={{
                          backgroundColor: index === 0 ? 'var(--c-yellow)' : index === 1 ? '#9ca3af' : '#b45309',
                          color: '#000',
                        }}
                      >
                        {index + 1}
                      </div>
                      <span className={index === 0 ? 'font-medium' : ''}>{manager.Manager}</span>
                    </div>
                    <div className="font-bold" style={{ color: index === 0 ? 'var(--c-yellow)' : 'var(--c-grey)' }}>
                      {manager.Total_Captain_Points || 0} pts
                    </div>
                  </div>
                ))}

                {selectedManager && (
                  <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}>
                    <div className="text-sm mb-1" style={{ color: 'var(--c-grey)' }}>Your Captain Points</div>
                    <div className="flex justify-between">
                      <span>
                        {performanceData.captainPoints.findIndex(m => m.Manager === selectedManager) + 1}
                        {getOrdinalSuffix(performanceData.captainPoints.findIndex(m => m.Manager === selectedManager) + 1)} place
                      </span>
                      <span className="font-bold">
                        {performanceData.captainPoints.find(m => m.Manager === selectedManager)?.Total_Captain_Points || 0} pts
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center p-4" style={{ color: 'var(--c-grey)' }}>No data available</div>
            )}
          </div>

          {/* Loyalty Issues (Transfer Regrets) */}
          <div className="rounded-lg p-5" style={{ backgroundColor: 'var(--c-surface-1)', border: '1px solid var(--c-border)' }}>
            <h4 className="font-medium mb-3 flex items-center" style={{ color: 'var(--c-bad)' }}>
              <svg className="mr-2" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.5 12.5C19.5 11.12 20.62 10 22 10V9C22 5 21 4 17 4H7C3 4 2 5 2 9V9.5C3.38 9.5 4.5 10.62 4.5 12C4.5 13.38 3.38 14.5 2 14.5V15C2 19 3 20 7 20H17C21 20 22 19 22 15C20.62 15 19.5 13.88 19.5 12.5Z" stroke="var(--c-bad)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 14.75L15 8.75" stroke="var(--c-bad)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14.9945 14.75H15.0035" stroke="var(--c-bad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8.99451 9.25H9.00349" stroke="var(--c-bad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Loyalty Issues
            </h4>

            <p className="text-xs mt-1 mb-3" style={{ color: 'var(--c-grey)' }}>Points missed by selling players right before they scored big hauls.</p>

            {performanceData.loyaltyIssues.length > 0 ? (
              <div className="space-y-3">
                {performanceData.loyaltyIssues.slice(0, 3).map((manager, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg"
                    style={{
                      backgroundColor: index === 0 ? 'rgba(235,87,87,0.12)' : 'var(--c-surface-2)',
                      border: `1px solid ${index === 0 ? 'var(--c-bad)' : 'var(--c-border)'}`,
                    }}
                  >
                    <div className="flex items-center">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center mr-2 text-xs font-bold"
                        style={{
                          backgroundColor: index === 0 ? 'var(--c-bad)' : 'var(--c-surface-3)',
                          color: index === 0 ? 'white' : 'var(--c-grey)',
                        }}
                      >
                        {index + 1}
                      </div>
                      <span className={index === 0 ? 'font-medium' : ''}>{manager.Manager}</span>
                    </div>
                    <div className="font-bold" style={{ color: index === 0 ? 'var(--c-bad)' : 'var(--c-grey)' }}>
                      {manager.Total_Points_Missed || 0} pts
                    </div>
                  </div>
                ))}

                {selectedManager && (
                  <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}>
                    <div className="text-sm mb-1" style={{ color: 'var(--c-grey)' }}>Your Loyalty Issues</div>
                    <div className="flex justify-between">
                      <span>
                        {performanceData.loyaltyIssues.findIndex(m => m.Manager === selectedManager) + 1}
                        {getOrdinalSuffix(performanceData.loyaltyIssues.findIndex(m => m.Manager === selectedManager) + 1)} place
                      </span>
                      <span className="font-bold">
                        {performanceData.loyaltyIssues.find(m => m.Manager === selectedManager)?.Total_Points_Missed || 0} pts
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center p-4" style={{ color: 'var(--c-grey)' }}>No data available</div>
            )}
          </div>

          {/* Differential King */}
          <div className="rounded-lg p-5" style={{ backgroundColor: 'var(--c-surface-1)', border: '1px solid var(--c-border)' }}>
            <h4 className="font-medium mb-3 flex items-center" style={{ color: 'var(--c-turquoise)' }}>
              <svg className="mr-2" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 22H22" stroke="var(--c-turquoise)" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9.75 4V22H14.25V4C14.25 2.9 13.8 2 12.45 2H11.55C10.2 2 9.75 2.9 9.75 4Z" stroke="var(--c-turquoise)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 10V22H7V10C7 8.9 6.6 8 5.4 8H4.6C3.4 8 3 8.9 3 10Z" stroke="var(--c-turquoise)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17 15V22H21V15C21 13.9 20.6 13 19.4 13H18.6C17.4 13 17 13.9 17 15Z" stroke="var(--c-turquoise)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Differential King
            </h4>

            <p className="text-xs mt-1 mb-3" style={{ color: 'var(--c-grey)' }}>Managers who gained the most points from low-ownership differential players.</p>

            {performanceData.differentialPoints.length > 0 ? (
              <div className="space-y-3">
                {performanceData.differentialPoints.slice(0, 3).map((manager, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg"
                    style={{
                      backgroundColor: index === 0 ? 'rgba(68,213,163,0.12)' : 'var(--c-surface-2)',
                      border: `1px solid ${index === 0 ? 'var(--c-turquoise)' : 'var(--c-border)'}`,
                    }}
                  >
                    <div className="flex items-center">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center mr-2 text-xs font-bold"
                        style={{
                          backgroundColor: index === 0 ? 'var(--c-turquoise)' : 'var(--c-surface-3)',
                          color: index === 0 ? '#000' : 'var(--c-grey)',
                        }}
                      >
                        {index + 1}
                      </div>
                      <span className={index === 0 ? 'font-medium' : ''}>{manager.Manager}</span>
                    </div>
                    <div className="font-bold" style={{ color: index === 0 ? 'var(--c-turquoise)' : 'var(--c-grey)' }}>
                      {manager.Differential_Points || 0} pts
                    </div>
                  </div>
                ))}

                {selectedManager && (
                  <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}>
                    <div className="text-sm mb-1" style={{ color: 'var(--c-grey)' }}>Your Differential Points</div>
                    <div className="flex justify-between">
                      <span>
                        {performanceData.differentialPoints.findIndex(m => m.Manager === selectedManager) + 1}
                        {getOrdinalSuffix(performanceData.differentialPoints.findIndex(m => m.Manager === selectedManager) + 1)} place
                      </span>
                      <span className="font-bold">
                        {performanceData.differentialPoints.find(m => m.Manager === selectedManager)?.Differential_Points || 0} pts
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center p-4" style={{ color: 'var(--c-grey)' }}>No data available</div>
            )}
          </div>

          {/* Most Improved */}
          <div className="rounded-lg p-5" style={{ backgroundColor: 'var(--c-surface-1)', border: '1px solid var(--c-border)' }}>
            <h4 className="font-medium mb-3 flex items-center" style={{ color: 'var(--c-yellow)' }}>
              <TrendingUp className="mr-2" size={16} />
              Most Improved (by points)
            </h4>

            <p className="text-xs mt-1 mb-3" style={{ color: 'var(--c-grey)' }}>Biggest improvement in point total from first half to second half of the season.</p>

            {performanceData.mostImproved.length > 0 ? (
              <div className="space-y-3">
                {performanceData.mostImproved.slice(0, 3).map((manager, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg"
                    style={{
                      backgroundColor: index === 0 ? 'rgba(242,201,76,0.12)' : 'var(--c-surface-2)',
                      border: `1px solid ${index === 0 ? 'var(--c-yellow)' : 'var(--c-border)'}`,
                    }}
                  >
                    <div className="flex items-center">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center mr-2 text-xs font-bold"
                        style={{
                          backgroundColor: index === 0 ? 'var(--c-yellow)' : index === 1 ? '#9ca3af' : '#b45309',
                          color: '#000',
                        }}
                      >
                        {index + 1}
                      </div>
                      <span className={index === 0 ? 'font-medium' : ''}>{manager.Manager}</span>
                    </div>
                    <div className="font-bold" style={{ color: index === 0 ? 'var(--c-yellow)' : 'var(--c-grey)' }}>
                      {Number(manager.Points_Difference) > 0 ? '+' : ''}{manager.Points_Difference || 0} pts
                    </div>
                  </div>
                ))}

                {selectedManager && (
                  <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}>
                    <div className="text-sm mb-1" style={{ color: 'var(--c-grey)' }}>Your Improvement</div>
                    <div className="flex justify-between">
                      <span>
                        {performanceData.mostImproved.findIndex(m => m.Manager === selectedManager) + 1}
                        {getOrdinalSuffix(performanceData.mostImproved.findIndex(m => m.Manager === selectedManager) + 1)} place
                      </span>
                      <span className="font-bold">
                        {performanceData.mostImproved.find(m => m.Manager === selectedManager)?.Points_Difference > 0 ? '+' : ''}
                        {performanceData.mostImproved.find(m => m.Manager === selectedManager)?.Points_Difference || 0} pts
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center p-4" style={{ color: 'var(--c-grey)' }}>No data available</div>
            )}
          </div>

          {/* 12th Man Specialist */}
          <div className="rounded-lg p-5" style={{ backgroundColor: 'var(--c-surface-1)', border: '1px solid var(--c-border)' }}>
            <h4 className="font-medium mb-3 flex items-center" style={{ color: 'var(--c-cyan)' }}>
              <Package className="mr-2" size={16} />
              12th Man Specialist
            </h4>

            <p className="text-xs mt-1 mb-3" style={{ color: 'var(--c-grey)' }}>Managers with the most points left on their bench throughout the season.</p>

            {performanceData.benchPoints.length > 0 ? (
              <div className="space-y-3">
                {performanceData.benchPoints.slice(0, 3).map((manager, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg"
                    style={{
                      backgroundColor: index === 0 ? 'rgba(68,182,197,0.12)' : 'var(--c-surface-2)',
                      border: `1px solid ${index === 0 ? 'var(--c-cyan)' : 'var(--c-border)'}`,
                    }}
                  >
                    <div className="flex items-center">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center mr-2 text-xs font-bold"
                        style={{
                          backgroundColor: index === 0 ? 'var(--c-cyan)' : 'var(--c-surface-3)',
                          color: index === 0 ? '#000' : 'var(--c-grey)',
                        }}
                      >
                        {index + 1}
                      </div>
                      <span className={index === 0 ? 'font-medium' : ''}>{manager.Manager}</span>
                    </div>
                    <div className="font-bold" style={{ color: index === 0 ? 'var(--c-cyan)' : 'var(--c-grey)' }}>
                      {manager.Points_on_Bench || 0} pts
                    </div>
                  </div>
                ))}

                {selectedManager && (
                  <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}>
                    <div className="text-sm mb-1" style={{ color: 'var(--c-grey)' }}>Your Bench Points</div>
                    <div className="flex justify-between">
                      <span>
                        {performanceData.benchPoints.findIndex(m => m.Manager === selectedManager) + 1}
                        {getOrdinalSuffix(performanceData.benchPoints.findIndex(m => m.Manager === selectedManager) + 1)} place
                      </span>
                      <span className="font-bold">
                        {performanceData.benchPoints.find(m => m.Manager === selectedManager)?.Points_on_Bench || 0} pts
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center p-4" style={{ color: 'var(--c-grey)' }}>No data available</div>
            )}
          </div>

          {/* The Tinkerman Award */}
          <TinkermanLeagueList />
        </div>

        <div className="flex justify-between mt-4">
          <button
            onClick={() => updateTab('heatmap')}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all"
            style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid var(--c-border)', color: 'var(--c-grey)' }}
          >
            <ChevronLeft size={16} /> Season Heatmap
          </button>
          <button
            onClick={() => updateTab('rivalry')}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white transition-all"
            style={{ backgroundColor: 'var(--c-blue)' }}
          >
            Rivalry <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
  
  // ─── Feature 7: League Rivalry Head-to-Head ───────────────────────────────
  const renderRivalryTab = () => {
    const weeklyPoints = data?.weeklyPoints || [];

    // Derive played gameweeks: integer keys 1–38 where at least one manager has a non-zero score.
    // We inspect the first row's keys to find available GW columns.
    const playedGWs = (() => {
      if (weeklyPoints.length === 0) return [];
      const firstRow = weeklyPoints[0];
      return Object.keys(firstRow)
        .map((k) => parseInt(k, 10))
        .filter((k) => !isNaN(k) && k >= 1 && k <= 38)
        .sort((a, b) => a - b)
        .filter((gw) =>
          weeklyPoints.some((row) => {
            const score = Number(row[gw]);
            return !isNaN(score) && score !== 0;
          })
        );
    })();

    // All managers except the selected one are valid rivals
    const allManagers = getManagers ? getManagers() : weeklyPoints.map((r) => r.Manager);
    const rivals = allManagers.filter((m) => m !== selectedManager);

    // Resolve row objects for selected manager and current rival
    const myRow = weeklyPoints.find((r) => r.Manager === selectedManager);
    const rivalRow = rivalryRival ? weeklyPoints.find((r) => r.Manager === rivalryRival) : null;

    // Per-GW transfer cost lookups (deducted from raw scores to get net GW points)
    const transferCostsGW = data?.transferCostsGW || [];
    const myTransferRow = transferCostsGW.find((r) => r.Manager === selectedManager) || {};
    const rivalTransferRow = rivalryRival
      ? (transferCostsGW.find((r) => r.Manager === rivalryRival) || {})
      : {};

    // Per-GW comparison data — only computed when both parties are known
    const gwResults = (myRow && rivalRow)
      ? playedGWs.map((gw) => {
          const myScore = (Number(myRow[gw]) || 0) - (Number(myTransferRow[gw]) || 0);
          const rivalScore = (Number(rivalRow[gw]) || 0) - (Number(rivalTransferRow[gw]) || 0);
          const winner = myScore > rivalScore ? 'me' : rivalScore > myScore ? 'rival' : 'draw';
          return { gw, myScore, rivalScore, winner };
        })
      : [];

    // Summary counts
    const gwsWonByMe = gwResults.filter((r) => r.winner === 'me').length;
    const gwsWonByRival = gwResults.filter((r) => r.winner === 'rival').length;
    const gwsDrawn = gwResults.filter((r) => r.winner === 'draw').length;

    // Cumulative score difference for the momentum chart
    // Positive values mean you are ahead; negative means the rival is ahead.
    const cumulativeDiffs = (() => {
      let running = 0;
      return gwResults.map(({ gw, myScore, rivalScore }) => {
        running += myScore - rivalScore;
        return { gw, cumDiff: running };
      });
    })();

    // Scale bar heights relative to the largest absolute cumulative value
    const maxAbsDiff = cumulativeDiffs.reduce(
      (max, { cumDiff }) => Math.max(max, Math.abs(cumDiff)),
      1 // avoid division by zero
    );

    // Shared tab-card style
    const cardStyle = {
      backgroundColor: 'var(--c-surface-2)',
      border: '1px solid var(--c-border)',
      borderRadius: '12px',
      padding: '24px',
    };

    // Chip style factory for rival picker buttons
    const chipStyle = (isActive) => ({
      padding: '6px 14px',
      borderRadius: '999px',
      fontSize: '0.8rem',
      fontWeight: isActive ? 600 : 400,
      cursor: 'pointer',
      border: `1px solid ${isActive ? 'var(--c-turquoise)' : 'var(--c-border)'}`,
      backgroundColor: isActive ? 'rgba(68,213,163,0.15)' : 'var(--c-surface-3)',
      color: isActive ? 'var(--c-turquoise)' : 'white',
      transition: 'all 0.18s ease',
      whiteSpace: 'nowrap',
    });

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* ── Section 1: Rival Picker ─────────────────────────────────── */}
        <div style={cardStyle}>
          <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Swords size={18} style={{ color: 'var(--c-turquoise)' }} />
            Choose Your Rival
          </h3>

          {rivals.length === 0 ? (
            <p style={{ color: 'var(--c-grey)', fontSize: '0.875rem' }}>No other managers found in the league.</p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {rivals.map((rival) => (
                <button
                  key={rival}
                  onClick={() => setRivalryRival(rival === rivalryRival ? null : rival)}
                  style={chipStyle(rival === rivalryRival)}
                >
                  {rival}
                </button>
              ))}
            </div>
          )}

          {!selectedManager && (
            <p style={{ color: 'var(--c-bad)', fontSize: '0.8rem', marginTop: '12px' }}>
              Select a manager from the Welcome screen to use this feature.
            </p>
          )}
        </div>

        {/* ── Section 2–4: Only shown when a rival is selected ─────────── */}
        <AnimatePresence>
          {rivalRow && myRow && (
            <motion.div
              key="rivalry-content"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 16 }}
              transition={{ duration: 0.3 }}
              style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
            >
              {/* ── Section 2: Summary Strip ─────────────────────────────── */}
              <div style={cardStyle}>
                <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px' }}>
                  {selectedManager} vs {rivalryRival}
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {/* GWs won by you */}
                  <div style={{
                    backgroundColor: 'var(--c-surface-3)',
                    border: '1px solid var(--c-blue)',
                    borderRadius: '10px',
                    padding: '16px 12px',
                    textAlign: 'center',
                  }}>
                    <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--c-blue)' }}>
                      {gwsWonByMe}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--c-grey)', marginTop: '4px', lineHeight: 1.3 }}>
                      GWs won<br />by you
                    </div>
                  </div>

                  {/* GWs drawn */}
                  <div style={{
                    backgroundColor: 'var(--c-surface-3)',
                    border: '1px solid var(--c-border)',
                    borderRadius: '10px',
                    padding: '16px 12px',
                    textAlign: 'center',
                  }}>
                    <div style={{ fontSize: '2rem', fontWeight: 800, color: 'white' }}>
                      {gwsDrawn}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--c-grey)', marginTop: '4px', lineHeight: 1.3 }}>
                      Draws
                    </div>
                  </div>

                  {/* GWs won by rival */}
                  <div style={{
                    backgroundColor: 'var(--c-surface-3)',
                    border: '1px solid var(--c-bad)',
                    borderRadius: '10px',
                    padding: '16px 12px',
                    textAlign: 'center',
                  }}>
                    <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--c-bad)' }}>
                      {gwsWonByRival}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--c-grey)', marginTop: '4px', lineHeight: 1.3 }}>
                      GWs won<br />by {rivalryRival}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Section 3: Season Momentum Chart ─────────────────────── */}
              <div style={cardStyle}>
                <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 700, marginBottom: '4px' }}>
                  Season Momentum
                </h3>
                <p style={{ color: 'var(--c-grey)', fontSize: '0.78rem', marginBottom: '16px' }}>
                  Cumulative score difference vs {rivalryRival}. Above the line = you were ahead.
                </p>

                {/* Legend */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '0.75rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'var(--c-grey)' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: 'var(--c-blue)', display: 'inline-block' }} />
                    You ahead
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'var(--c-grey)' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: 'var(--c-bad)', display: 'inline-block' }} />
                    {rivalryRival} ahead
                  </span>
                </div>

                {/* Bar chart — single zero-line, bars grow up (positive) or down (negative) */}
                <div
                  style={{ display: 'flex', gap: '2px', height: '100px', position: 'relative' }}
                  onMouseLeave={() => setHoveredMomentum(null)}
                >
                  {/* Single zero baseline at centre */}
                  <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', height: '1px', backgroundColor: 'var(--c-border)', zIndex: 0 }} />

                  {/* Hover tooltip */}
                  {hoveredMomentum && (
                    <div style={{
                      position: 'absolute',
                      left: `clamp(0px, calc(${hoveredMomentum.idx / hoveredMomentum.total * 100}% - 52px), calc(100% - 104px))`,
                      top: hoveredMomentum.cumDiff >= 0 ? '2px' : '54px',
                      zIndex: 10,
                      backgroundColor: 'var(--c-surface-1)',
                      border: `1px solid ${hoveredMomentum.cumDiff >= 0 ? 'var(--c-blue)' : 'var(--c-bad)'}`,
                      borderRadius: '6px',
                      padding: '5px 10px',
                      pointerEvents: 'none',
                      whiteSpace: 'nowrap',
                      minWidth: '104px',
                      textAlign: 'center',
                    }}>
                      <div style={{ fontSize: '0.65rem', color: 'var(--c-grey)', marginBottom: '2px' }}>GW{hoveredMomentum.gw}</div>
                      <div style={{ fontSize: '0.85rem', fontWeight: 700, color: hoveredMomentum.cumDiff >= 0 ? 'var(--c-turquoise)' : 'var(--c-bad)' }}>
                        {hoveredMomentum.cumDiff >= 0 ? '+' : ''}{hoveredMomentum.cumDiff} pts
                      </div>
                      <div style={{ fontSize: '0.6rem', color: 'var(--c-grey)', marginTop: '1px' }}>
                        {hoveredMomentum.cumDiff >= 0 ? 'you ahead' : `${rivalryRival} ahead`}
                      </div>
                    </div>
                  )}

                  {cumulativeDiffs.map(({ gw, cumDiff }, idx) => {
                    const barPx = maxAbsDiff > 0 ? Math.max(cumDiff !== 0 ? 2 : 0, Math.round(Math.abs(cumDiff) / maxAbsDiff * 48)) : 0;
                    const isPositive = cumDiff >= 0;
                    const isHovered = hoveredMomentum?.gw === gw;
                    return (
                      <div
                        key={gw}
                        style={{ flex: 1, position: 'relative', zIndex: 1, minWidth: 0, cursor: 'crosshair' }}
                        onMouseEnter={() => setHoveredMomentum({ gw, cumDiff, idx, total: cumulativeDiffs.length })}
                      >
                        <motion.div
                          initial={{ scaleY: 0 }}
                          animate={{ scaleY: 1 }}
                          transition={{ duration: 0.4, delay: idx * 0.012 }}
                          style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            height: `${barPx}px`,
                            backgroundColor: isPositive ? 'var(--c-blue)' : 'var(--c-bad)',
                            borderRadius: isPositive ? '2px 2px 0 0' : '0 0 2px 2px',
                            ...(isPositive ? { bottom: '50%' } : { top: '50%' }),
                            transformOrigin: isPositive ? 'bottom' : 'top',
                            opacity: isHovered ? 1 : 0.75,
                            transition: 'opacity 0.1s',
                          }}
                        />
                      </div>
                    );
                  })}
                </div>

                {/* GW axis labels */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                  {cumulativeDiffs
                    .filter((_, idx) => idx === 0 || (idx + 1) % 5 === 0 || idx === cumulativeDiffs.length - 1)
                    .map(({ gw }) => (
                      <span key={gw} style={{ color: 'var(--c-grey)', fontSize: '0.65rem' }}>GW{gw}</span>
                    ))}
                </div>

                {/* Final verdict */}
                {cumulativeDiffs.length > 0 && (
                  <div style={{
                    marginTop: '16px', padding: '12px', borderRadius: '8px',
                    backgroundColor: 'var(--c-surface-3)',
                    border: `1px solid ${cumulativeDiffs[cumulativeDiffs.length - 1].cumDiff >= 0 ? 'var(--c-blue)' : 'var(--c-bad)'}`,
                    textAlign: 'center', fontSize: '0.85rem', color: 'white',
                  }}>
                    {(() => {
                      const finalDiff = cumulativeDiffs[cumulativeDiffs.length - 1].cumDiff;
                      if (finalDiff > 0) return <>Overall you beat <strong>{rivalryRival}</strong> by <span style={{ color: 'var(--c-turquoise)', fontWeight: 700 }}>+{finalDiff} pts</span> across all played gameweeks.</>;
                      if (finalDiff < 0) return <><strong>{rivalryRival}</strong> beat you overall by <span style={{ color: 'var(--c-bad)', fontWeight: 700 }}>{Math.abs(finalDiff)} pts</span> across all played gameweeks.</>;
                      return <>You and <strong>{rivalryRival}</strong> are perfectly level across all played gameweeks.</>;
                    })()}
                  </div>
                )}
              </div>

              {/* ── Section 4: Week-by-Week Table ────────────────────────── */}
              <div style={cardStyle}>
                <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px' }}>
                  Gameweek Breakdown
                </h3>

                {/* Column headings */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '48px 1fr 28px 1fr 80px',
                  gap: '8px',
                  paddingBottom: '8px',
                  borderBottom: '1px solid var(--c-border)',
                  marginBottom: '4px',
                }}>
                  <span style={{ color: 'var(--c-grey)', fontSize: '0.7rem', fontWeight: 600 }}>GW</span>
                  <span style={{ color: 'var(--c-grey)', fontSize: '0.7rem', fontWeight: 600, textAlign: 'right' }}>You</span>
                  <span />
                  <span style={{ color: 'var(--c-grey)', fontSize: '0.7rem', fontWeight: 600 }}>{rivalryRival}</span>
                  <span style={{ color: 'var(--c-grey)', fontSize: '0.7rem', fontWeight: 600, textAlign: 'center' }}>Winner</span>
                </div>

                {/* Scrollable rows */}
                <div style={{ maxHeight: '320px', overflowY: 'auto', paddingRight: '4px' }}>
                  {gwResults.map(({ gw, myScore, rivalScore, winner }, idx) => (
                    <motion.div
                      key={gw}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2, delay: idx * 0.015 }}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '48px 1fr 28px 1fr 80px',
                        gap: '8px',
                        alignItems: 'center',
                        padding: '7px 0',
                        borderBottom: '1px solid rgba(74,74,74,0.35)',
                      }}
                    >
                      <span style={{ color: 'var(--c-grey)', fontSize: '0.8rem' }}>GW{gw}</span>
                      <span style={{ textAlign: 'right', fontWeight: winner === 'me' ? 700 : 400, color: winner === 'me' ? 'var(--c-turquoise)' : 'white', fontSize: '0.9rem' }}>{myScore}</span>
                      <span style={{ color: 'var(--c-grey)', fontSize: '0.7rem', textAlign: 'center' }}>vs</span>
                      <span style={{ fontWeight: winner === 'rival' ? 700 : 400, color: winner === 'rival' ? 'var(--c-bad)' : 'white', fontSize: '0.9rem' }}>{rivalScore}</span>
                      <span style={{
                        textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, padding: '2px 6px', borderRadius: '4px',
                        backgroundColor: winner === 'me' ? 'rgba(68,213,163,0.15)' : winner === 'rival' ? 'rgba(235,87,87,0.15)' : 'rgba(138,138,138,0.15)',
                        color: winner === 'me' ? 'var(--c-turquoise)' : winner === 'rival' ? 'var(--c-bad)' : 'var(--c-grey)',
                      }}>
                        {winner === 'me' ? 'You' : winner === 'rival' ? rivalryRival : 'Draw'}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Prompt to pick a rival when none is selected */}
        {!rivalryRival && rivals.length > 0 && selectedManager && (
          <div style={{ textAlign: 'center', color: 'var(--c-grey)', fontSize: '0.875rem', padding: '24px 0' }}>
            Select a rival above to see your head-to-head record.
          </div>
        )}

        {/* Navigation footer */}
        <div className="flex justify-between mt-4">
          <button
            onClick={() => updateTab('specialStats')}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all"
            style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid var(--c-border)', color: 'var(--c-grey)' }}
          >
            <ChevronLeft size={16} /> Special Stats
          </button>
          <button
            onClick={() => updateTab('constellation')}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white transition-all"
            style={{ backgroundColor: 'var(--c-blue)' }}
          >
            Constellation <ArrowRight size={14} />
          </button>
        </div>
      </div>
    );
  };
  // ──────────────────────────────────────────────────────────────────────────

  // Main render function
  return (
    <div ref={pageRef} className="w-full h-full overflow-auto text-white p-6" style={{ backgroundColor: 'var(--c-black)' }}>
      <div className="max-w-3xl mx-auto pt-8">
        <h2 className="text-3xl font-bold mb-2 text-center">League Overview</h2>
        <p className="text-center mb-8" style={{ color: 'var(--c-grey)' }}>Explore how all managers performed this season</p>

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="rounded-full p-1 flex overflow-x-auto" style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}>
            {[
              { id: 'overview',       label: 'Overview' },
              { id: 'leaderboard',    label: 'Leaderboard' },
              { id: 'heatmap',        label: 'Season Heatmap' },
              { id: 'specialStats',   label: 'Special Stats' },
              { id: 'rivalry',        label: 'Rivalry' },
              { id: 'constellation',  label: 'Constellation' },
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => updateTab(id)}
                className="px-4 py-2 rounded-full whitespace-nowrap text-sm transition-all"
                style={
                  activeTab === id
                    ? { backgroundColor: 'var(--c-blue)', color: 'white', fontWeight: 600 }
                    : { color: 'var(--c-grey)', background: 'transparent' }
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Constellation tab is full-width — outside the narrow container */}
        {activeTab === 'constellation' && (
          <>
            <div style={{ margin: '0 -24px' }}>
              <LeagueConstellation />
            </div>
            <div className="flex justify-between mt-4">
              <button
                onClick={() => updateTab('rivalry')}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid var(--c-border)', color: 'var(--c-grey)' }}
              >
                <ChevronLeft size={16} /> Rivalry
              </button>
              <button
                onClick={goToMyJourney}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white transition-all"
                style={{ backgroundColor: 'var(--c-blue)' }}
              >
                My Journey <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}

        {/* Tab content */}
        {activeTab !== 'constellation' && (
          <div className="pb-8">
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'leaderboard' && renderLeaderboardTab()}
            {activeTab === 'heatmap' && renderHeatmapTab()}
            {activeTab === 'specialStats' && renderSpecialStatsTab()}
            {activeTab === 'rivalry' && renderRivalryTab()}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeagueOverview;
